import {Button, Grid, Heading} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useMemo} from 'react'

import {hpcAttrs} from '@github-ui/hpc'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {DecoratedDownloadActionMenu} from '../../../../../brand/components/DecoratedDownloadActionMenu/DecoratedDownloadActionMenu'
import type {Cta, DownloadMenu, SignUpField} from '../../../Index.types'
import {MonaHead, type MonaHeadProps} from '../MonaHead/MonaHead'
import {EyebrowBanner, type EyebrowBannerProps} from '../EyebrowBanner/EyebrowBanner'
import {SignUpForm} from '../SignUpForm/SignUpForm'

import Styles from './Intro.module.css'

export interface IntroProps {
  eyebrowBanner?: EyebrowBannerProps
  title: string
  loggedIn: boolean
  isOrgAdmin: boolean
  ctas: {
    trial: Cta
    sales: Cta
    signUp: SignUpField
    downloadCta: Cta
    downloadMenu: DownloadMenu
  }
  monaHead: MonaHeadProps
}

export const defaultProps: Partial<IntroProps> = {}

export const Intro = (props: IntroProps) => {
  const initializedProps: IntroProps = {...defaultProps, ...props}
  const {eyebrowBanner, title, loggedIn, isOrgAdmin, ctas, monaHead} = initializedProps
  const downloadActions = useMemo(
    () => ctas.downloadMenu.actions.map(({url, ...action}) => ({...action, href: url})),
    [ctas.downloadMenu.actions],
  )

  return (
    <div className={Styles['Intro']}>
      <div className={Styles['Intro__grid_content']}>
        <Grid columnGap="none" rowGap="none" enableGutters={false}>
          <Grid.Column span={12} className={Styles['Intro__grid_column']}>
            <div className={Styles['Intro__content']}>
              <div className={Styles['Intro__header']}>
                {eyebrowBanner?.url && <EyebrowBanner {...eyebrowBanner} />}

                <Heading
                  as="h1"
                  size="1"
                  weight="medium"
                  textWrap="balance"
                  className={Styles['Intro__header_title']}
                  {...hpcAttrs()}
                >
                  {title}
                </Heading>
              </div>

              <div className={clsx(Styles.Intro__ctas, !loggedIn && Styles['Intro__ctas--signUp'])}>
                {!loggedIn ? (
                  <>
                    <SignUpForm
                      className={Styles['Intro__ctas_signUpForm']}
                      signUp={ctas.signUp}
                      formLabel={ctas.signUp.cta.label}
                      location="intro"
                    />
                    <Button
                      as="a"
                      href={ctas.downloadCta.url}
                      className={clsx(Styles.Intro__ctas_button, Styles['Intro__ctas_button--signUpSecondary'])}
                      variant="secondary"
                      {...getAnalyticsEvent({
                        action: ctas.downloadCta.label,
                        tag: 'a',
                        context: 'CTAs',
                        location: 'intro',
                      })}
                    >
                      {ctas.downloadCta.label}
                    </Button>
                  </>
                ) : isOrgAdmin ? (
                  <>
                    <Button
                      as="a"
                      href={ctas.trial.url}
                      className={Styles['Intro__ctas_button']}
                      variant="primary"
                      {...getAnalyticsEvent({
                        action: ctas.trial.label,
                        tag: 'a',
                        context: 'CTAs',
                        location: 'intro',
                      })}
                    >
                      {ctas.trial.label}
                    </Button>
                    <Button
                      as="a"
                      href={ctas.sales.url}
                      className={Styles['Intro__ctas_button']}
                      variant="secondary"
                      {...getAnalyticsEvent({
                        action: ctas.sales.label,
                        tag: 'a',
                        context: 'CTAs',
                        location: 'intro',
                      })}
                    >
                      {ctas.sales.label}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className={Styles['Intro__ctas_downloadMenu']}>
                      <DecoratedDownloadActionMenu
                        actions={downloadActions}
                        menuLabel={ctas.downloadMenu.menuLabel}
                        location="intro"
                      />
                    </div>
                    <Button
                      as="a"
                      href={ctas.trial.url}
                      variant="secondary"
                      className={Styles['Intro__ctas_button']}
                      {...getAnalyticsEvent({
                        action: ctas.trial.label,
                        tag: 'a',
                        context: 'CTAs',
                        location: 'intro',
                      })}
                    >
                      {ctas.trial.label}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className={Styles['Intro__mona']}>
              <MonaHead {...monaHead} />
            </div>
          </Grid.Column>
        </Grid>
      </div>
    </div>
  )
}
